import moment from 'moment';
import { buildDemoDiary, TRADES, demoServiceColours } from '../../demo/diaryDemo';
import { SETUP_V2_VISIBLE } from '../../../config/featureFlags';

/**
 * Bailey's welcome: what the diary shows at each beat.
 *
 * The intro drives the real diary rather than a mock of it, so the owner is
 * looking at the screen they will use every day. Nothing is written: like the
 * staging diary demo, the diary response is reshaped in the browser before it
 * renders, and turning the stage off brings the real diary straight back.
 *
 *   blank  the owner's own column, empty and open for the day
 *   team   plus two example colleagues, still empty
 *   fill   every column filled with practice bookings in the chosen palette
 *
 * Ships with "Set up with Bailey" (SETUP_V2_VISIBLE).
 */

const KEY = 'beu:bailey-intro';
const DONE_KEY = 'beu:bailey-intro-done';
export const INTRO_EVENT = 'beu:bailey-intro-changed';
export const INTRO_START_EVENT = 'beu:bailey-intro-start';
export const BAILEY_INTRO_ENABLED = SETUP_V2_VISIBLE;

const DEFAULTS = { stage: 'off', trade: 'salon', palette: 'Coastal' };

export function getIntro() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
  } catch (e) {
    return { ...DEFAULTS };
  }
}

export function setIntro(patch) {
  const next = { ...getIntro(), ...patch };
  try {
    if (next.stage === 'off') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify(next));
  } catch (e) { /* storage unavailable */ }
  try { window.dispatchEvent(new CustomEvent(INTRO_EVENT, { detail: next })); } catch (e) { /* ignore */ }
  return next;
}

export const isIntroActive = () => BAILEY_INTRO_ENABLED && getIntro().stage !== 'off';

// Per person, so a second account on the same browser still gets its welcome.
const doneKey = () => `${DONE_KEY}:${localStorage.getItem('userId') || 'anon'}`;
export const isIntroDone = () => { try { return localStorage.getItem(doneKey()) === '1'; } catch (e) { return true; } };
export const markIntroDone = () => { try { localStorage.setItem(doneKey(), '1'); } catch (e) { /* ignore */ } };
export const resetIntroDone = () => { try { localStorage.removeItem(doneKey()); } catch (e) { /* ignore */ } };

/** Booking ids made up by the demo generator. Nothing exists behind them. */
export const isDemoBookingId = (id) => typeof id === 'string' && id.indexOf('demo-') === 0;

const emptyDay = (start, end) => ({ isWorking: true, rota: { startTime: start, endTime: end }, bookings: [], events: [], holidays: [] });

// The owner's column: their own record when it can be told apart, otherwise
// the first column the diary would show.
function ownerOf(staff) {
  const userId = localStorage.getItem('userId');
  return staff.find((s) => s.id === userId)
    || staff.find((s) => s.isSalonOwner === 1 || s.isSalonOwner === true)
    || staff[0]
    || null;
}

// The generator closes on Sundays; the intro should never land on a shut
// salon, so a Sunday borrows Saturday's shape.
const seedDate = (dateStr) => {
  const d = moment(dateStr, 'YYYY-MM-DD');
  return d.isoWeekday() === 7 ? d.clone().subtract(1, 'day').format('YYYY-MM-DD') : dateStr;
};

// How many real columns the salon had the last time the intro reshaped the
// day. A brand new salon has one (the owner), and only then do the example
// colleagues stay on after the intro.
let lastRealStaffCount = 1;
export const getLastRealStaffCount = () => lastRealStaffCount;

// The day as the intro last drew it, so checkout can open a practice booking
// without asking the server for something that does not exist there.
let lastDay = {};
let lastStaff = [];

/**
 * Reshapes a diary API response for the current stage. Returns the response
 * untouched when the intro is not running.
 */
export function applyIntroStage(diaryData, dateStr) {
  if (!isIntroActive() || !diaryData) return diaryData;
  try {
    const { stage, trade, palette } = getIntro();
    const cfg = TRADES[trade] || TRADES.salon;
    const realStaff = diaryData.staff || [];
    lastRealStaffCount = realStaff.length;
    const owner = ownerOf(realStaff);
    const ownerShape = cfg.staff[0];
    const ownerStaff = owner || { id: 'demo-owner', name: 'You', firstName: 'You', lastName: '', avatar: '', isSalonOwner: 1 };

    const day = {};
    const staff = [ownerStaff];
    day[ownerStaff.id] = emptyDay(ownerShape.start, ownerShape.end);

    if (stage === 'team' || stage === 'fill') {
      const from = seedDate(dateStr);
      const demo = buildDemoDiary({ startDate: from, endDate: from, override: { trade, palette, density: 'steady' } });
      const generated = (demo.dates && demo.dates[from]) || {};
      // From the mixed-team beat on, the headers say who is self-employed
      // and who is employed instead of just "Example".
      const labels = getIntro().labels ? ['Self-employed', 'Employed'] : [];
      const colleagues = (demo.staff || []).slice(1, 3).map((s, i) => ({ ...s, isOnboardingDemo: true, demoLabel: labels[i] || null }));

      // Everything reads as upcoming: a wall of struck-through "done"
      // bookings is the opposite of a diary coming to life.
      const upcoming = (d) => ({
        ...d,
        isWorking: true,
        bookings: (d.bookings || []).map((b) => ({ ...b, bookingStatus: 1 })),
      });

      // Room to book: about a third of the practice bookings go, and the
      // first colleague keeps a clear gap where Bailey asks for a booking.
      const { gap, added } = getIntro();
      const lighter = (d, isGapColumn) => ({
        ...d,
        bookings: (d.bookings || []).filter((b, i) => {
          if (i % 3 === 1) return false;
          if (isGapColumn && gap) {
            const [from, to] = b.bookingSlot || [];
            if (from < gap.to && to > gap.from) return false;
          }
          return true;
        }),
        events: (d.events || []).filter((e) => !(isGapColumn && gap && e.startTime < gap.to && e.endTime > gap.from)),
      });

      if (stage === 'fill') {
        const ownerDemo = generated[(demo.staff[0] || {}).id];
        if (ownerDemo) day[ownerStaff.id] = { ...lighter(upcoming(ownerDemo), false), rota: { startTime: ownerShape.start, endTime: ownerShape.end } };
      }
      colleagues.forEach((s, i) => {
        const shape = cfg.staff[i + 1];
        const d = generated[s.id];
        day[s.id] = stage === 'fill' && d ? lighter(upcoming(d), i === 0) : emptyDay(shape.start, shape.end);
        staff.push(s);
      });

      // Bookings made during the tour, drawn like any other.
      if (stage === 'fill') {
        (added || []).filter((b) => b.bookingDate === dateStr && day[b.staffId]).forEach((b) => {
          day[b.staffId] = { ...day[b.staffId], bookings: [...(day[b.staffId].bookings || []), toBooking(b)] };
        });
      }
    }

    lastDay = day;
    lastStaff = staff;
    return {
      ...diaryData,
      staff,
      dates: { [dateStr]: day },
      staffOrder: staff.map((s) => s.id),
      isBaileyIntro: true,
      realStaffCount: realStaff.length,
    };
  } catch (e) {
    return diaryData;
  }
}

// A booking made in the practice panel, in the shape the diary API returns.
function toBooking(b) {
  return {
    _id: b.id,
    bookingSlot: [b.startMinutes, b.endMinutes],
    bookingStatus: 1,
    isOnlineBooking: 0,
    clientId: b.clientId,
    user: { _id: b.clientId, name: b.clientName, avatar: '' },
    servicesBooked: (b.services || []).map((sv) => ({
      _id: sv.id, service: sv.id, serviceName: sv.name, servicePrice: sv.price, serviceTime: sv.time,
      processingTime: 0, setupTime: 0, cleanupTime: 0, diaryColour: sv.colour,
    })),
    resources: [],
    transaction: [],
  };
}

/** Saves a practice booking. Nothing leaves the browser. */
export function addIntroBooking(b) {
  const cur = getIntro();
  const id = `demo-added-${Date.now()}`;
  setIntro({ added: [...(cur.added || []), { ...b, id }] });
  return id;
}

/* ---------- the practice booking panel's clients and services ---------- */

/** Example clients for the booking panel, filtered by what was typed. */
export function introClients(query) {
  const cfg = TRADES[getIntro().trade] || TRADES.salon;
  const q = String(query || '').trim().toLowerCase();
  return cfg.clients
    .slice()
    .sort((a, b) => a.localeCompare(b))
    .filter((name) => !q || name.toLowerCase().includes(q))
    .slice(0, 30)
    .map((name, i) => ({ _id: `demo-client-${name.replace(/\s+/g, '-').toLowerCase()}`, name, _example: true, _order: i }));
}

/** Example services, as the one category the panel's picker expects. */
export function introServiceCategories() {
  const { trade, palette } = getIntro();
  const cfg = TRADES[trade] || TRADES.salon;
  const colours = demoServiceColours(trade, palette);
  return [{
    categoryName: 'Example services',
    hairdresserService: cfg.services.map((sv) => ({
      _id: `demo-svc-${sv.name.replace(/\s+/g, '-').toLowerCase()}`,
      serviceName: sv.name,
      servicePrice: sv.price,
      serviceTime: sv.time,
      diaryColour: colours[sv.name],
      processingTime: 0, setupTime: 0, cleanupTime: 0,
    })),
  }];
}

/** A practice booking in the shape checkout loads from the API. */
export function introCheckoutBooking(bookingId) {
  const staffIds = Object.keys(lastDay);
  for (let i = 0; i < staffIds.length; i += 1) {
    const staffId = staffIds[i];
    const b = ((lastDay[staffId] || {}).bookings || []).find((x) => x._id === bookingId);
    if (b) {
      const who = lastStaff.find((s) => s.id === staffId) || {};
      return {
        _id: b._id,
        hairdresserId: staffId,
        hairdresserName: who.name || '',
        user: b.user || { _id: b.clientId, name: 'Walk-in' },
        servicesBooked: (b.servicesBooked || []).map((sv) => ({
          serviceId: sv.service || sv._id,
          serviceName: sv.serviceName,
          servicePrice: sv.servicePrice,
          serviceTime: sv.serviceTime,
          diaryColour: sv.diaryColour,
          hairdresserId: staffId,
          hairdresserName: who.name || '',
        })),
        products: [],
        transaction: [],
        salonId: localStorage.getItem('salonId'),
        currencySymbol: '£',
      };
    }
  }
  return null;
}

/** Bailey asks the diary to open or close checkout on a practice booking. */
export const INTRO_CHECKOUT_EVENT = 'beu:bailey-intro-checkout';
export const introCheckout = (action, bookingId) => {
  try { window.dispatchEvent(new CustomEvent(INTRO_CHECKOUT_EVENT, { detail: { action, bookingId } })); } catch (e) { /* ignore */ }
};

// Retail for the practice checkout, per trade.
const DEMO_PRODUCTS = {
  salon: [
    ['Hydrating Shampoo 250ml', 18], ['Repair Conditioner 250ml', 18], ['Heat Protect Spray', 16],
    ['Texture Spray', 20], ['Curl Defining Cream', 17], ['Nourishing Hair Oil 50ml', 24],
  ],
  barber: [
    ['Matte Clay', 16], ['Classic Pomade', 15], ['Beard Oil 30ml', 14],
    ['Beard Balm', 13], ['Sea Salt Spray', 14], ['Daily Shampoo 250ml', 12],
  ],
  beauty: [
    ['Cuticle Oil', 9], ['Lash Growth Serum', 35], ['Brow Styling Gel', 12],
    ['Rich Hand Cream', 10], ['Hydrating Face Serum', 28], ['Daily SPF 50', 24],
  ],
};

/** Example products for the practice checkout. */
export function introProducts() {
  const list = DEMO_PRODUCTS[getIntro().trade] || DEMO_PRODUCTS.salon;
  return list.map(([name, price]) => ({
    _id: `demo-product-${name.replace(/\s+/g, '-').toLowerCase()}`,
    productName: name,
    retailPrice: price,
    stockPrice: 0,
    commissionEnable: 0,
    options: [],
    variants: [],
    trackStock: false,
  }));
}

/** Example gift cards for the practice checkout, in the catalogue shape. */
export function introVoucherCatalogs() {
  return [
    { _id: 'demo-voucher-gift', name: 'Gift card', value: 25, denominations: [25, 50, 100], customAmount: true, customMin: 10, customMax: 500 },
    { _id: 'demo-voucher-treat', name: 'Treat yourself voucher', value: 60, denominations: [60] },
  ];
}

/** Events the practice booking panel sends Bailey as the owner works through it. */
export const INTRO_BOOKING_EVENT = 'beu:bailey-intro-booking';
export const tellIntro = (step, detail) => {
  try { window.dispatchEvent(new CustomEvent(INTRO_BOOKING_EVENT, { detail: { step, ...(detail || {}) } })); } catch (e) { /* ignore */ }
};

/** The two example colleagues' first names, for Bailey to introduce. */
export function introColleagues(trade) {
  const cfg = TRADES[trade] || TRADES.salon;
  return cfg.staff.slice(1, 3).map((s) => s.name.split(' ')[0]);
}
